import { useState } from "react";
import { Link } from "@/src/lib/next-compat";
import { useSearchParams } from "react-router-dom";
import { FileText, CheckCircle2 } from "lucide-react";
import CheckoutSuccessNotifier from "@/components/student/CheckoutSuccessNotifier";
import InvoiceModal from "@/components/checkout/InvoiceModal";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const courseUuid = searchParams.get("course") ?? undefined;
  const orgSlug = searchParams.get("org") ?? undefined;
  const wsSlug = searchParams.get("ws") ?? undefined;
  const pkg = searchParams.get("pkg") ?? undefined;
  const transactionId = searchParams.get("transaction_id") ?? undefined;
  const ref = searchParams.get("ref") ?? searchParams.get("checkout_id") ?? undefined;

  const invoiceIdentifier = transactionId || ref;
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);

  return (
    <main className="min-h-screen bg-[var(--background)] px-4 py-16">
      {courseUuid ? <CheckoutSuccessNotifier courseUuid={courseUuid} /> : null}

      {invoiceIdentifier && (
        <InvoiceModal
          identifier={invoiceIdentifier}
          isOpen={isInvoiceOpen}
          onClose={() => setIsInvoiceOpen(false)}
        />
      )}

      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-semibold text-[var(--foreground)]">Payment successful</h1>
        <p className="mt-3 text-sm text-[var(--muted-foreground)]">
          {pkg && orgSlug
            ? "Your AI credit package purchase is complete. The credits have been credited to your organization balance and are ready for course generation."
            : orgSlug
            ? "Your organization course purchase is complete. Workspace members can now access this course."
            : "Your purchase is complete. You can open the course and continue learning."}
        </p>

        {invoiceIdentifier && (
          <div className="mt-6 p-4 rounded-xl border border-indigo-100 bg-indigo-50/50 dark:bg-indigo-950/20 dark:border-indigo-900/40 text-left flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                Official Tax Invoice Ready
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                ZATCA-compliant corporate receipt with VAT breakdown
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsInvoiceOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-700 dark:text-indigo-300 bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800 shadow-sm hover:bg-indigo-50 transition shrink-0"
            >
              <FileText className="w-3.5 h-3.5" />
              View Invoice
            </button>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {orgSlug ? (
            <Link
              to={`/org/${orgSlug}/dashboard`}
              className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-medium text-[var(--primary-foreground)]"
            >
              Back to Dashboard
            </Link>
          ) : courseUuid ? (
            <Link
              to={`/courses/${courseUuid}?prep=1`}
              className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-medium text-[var(--primary-foreground)]"
            >
              Go to course
            </Link>
          ) : null}
          <Link
            to={orgSlug ? `/org/${orgSlug}/dashboard` : "/courses"}
            className="rounded-lg border border-[var(--border)] px-5 py-2.5 text-sm font-medium text-[var(--foreground)]"
          >
            {orgSlug ? "Organization Workspace" : "All courses"}
          </Link>
        </div>
      </div>
    </main>
  );
}
